import { queryNoTracking, queryOther, queryOtherBlobUrl, throwIfFrameworkException, update, uploadFileInChunks } from '../../services/DataService';
import { safeString } from './useAiChatAssistant';

// ── Server-kept assistant sessions ──────────────────────────────────────────────
//
// A session is a row (AssistantSession: profile + host group, title, status, turn
// count, who touched it) and a document beside it in the tenant's AI document store
// (the dialog's state as last sent, the turn running or interrupted, the last turn's
// response). The dialog opens its host's group on open (openOrCreate), lists the
// group for the picker, and sends the row's key with every turn; the server keeps
// the document current as the turn runs, so a session interrupted anywhere resumes
// from what the server has — never from what the browser had.
//
// Reads go through DataController (AssistantSessions_*), writes through
// UpdateController (AssistantSession_*); each is gated by the profile's host access.

const q_List = queryNoTracking('AssistantSessions_List', 0, true);
const q_OpenOrCreate = queryNoTracking('AssistantSessions_OpenOrCreate', 0, true);
const q_Load = queryNoTracking('AssistantSessions_Load', 0, true);
const q_Documents = queryNoTracking('AssistantSessions_Documents', 0, true);

export const SESSION_STATUS_ACTIVE = 'active';
export const SESSION_STATUS_CLOSED = 'closed';
export const SESSION_STATUS_ARCHIVED = 'archived';

/**
 * The host group as the server stores it. hostEntityID 0 / null means "no record yet"
 * (an unsaved editor): such sessions group by profile + entity alone.
 */
export const normalizeSessionHost = (sessionHost, profile) => ({
  profile: safeString(profile ?? sessionHost?.profile).trim(),
  hostEntity: safeString(sessionHost?.hostEntity).trim() || null,
  hostEntityID: Number(sessionHost?.hostEntityID) > 0 ? Number(sessionHost.hostEntityID) : null,
  hostSubKey: safeString(sessionHost?.hostSubKey).trim() || null,
});

const toHostRequest = (host, extra = {}) => ({
  profile: host.profile,
  hostEntity: host.hostEntity,
  hostEntityID: host.hostEntityID,
  hostSubKey: host.hostSubKey,
  ...extra,
});

const parseDate = (v) => {
  const ms = Date.parse(safeString(v));
  return Number.isFinite(ms) ? new Date(ms) : null;
};

/** Normalizes an AssistantSessionInfo (PascalCase from the server) to the camelCase the UI reads. */
export const parseSessionInfo = (info) => (info ? {
  sessionKey: safeString(info.SessionKey ?? info.sessionKey),
  profile: safeString(info.Profile ?? info.profile),
  hostEntity: safeString(info.HostEntity ?? info.hostEntity) || null,
  hostEntityID: info.HostEntityID ?? info.hostEntityID ?? null,
  hostSubKey: safeString(info.HostSubKey ?? info.hostSubKey) || null,
  title: safeString(info.Title ?? info.title),
  status: safeString(info.Status ?? info.status).toLowerCase() || SESSION_STATUS_ACTIVE,
  turnCount: Number(info.TurnCount ?? info.turnCount ?? 0),
  lastPromptPreview: safeString(info.LastPromptPreview ?? info.lastPromptPreview),
  apiVersion: safeString(info.ApiVersion ?? info.apiVersion) || null,
  turnInProgress: !!(info.TurnInProgress ?? info.turnInProgress),
  createdOn: parseDate(info.CreatedOn ?? info.createdOn),
  createdBy: safeString(info.CreatedBy ?? info.createdBy),
  updatedOn: parseDate(info.UpdatedOn ?? info.updatedOn),
  updatedBy: safeString(info.UpdatedBy ?? info.updatedBy),
  lastTurnOn: parseDate(info.LastTurnOn ?? info.lastTurnOn),
} : null);

const parsePendingTurn = (p) => (p ? {
  turnID: safeString(p.TurnID ?? p.turnID),
  prompt: safeString(p.Prompt ?? p.prompt),
  intent: safeString(p.Intent ?? p.intent),
  images: Number(p.Images ?? p.images ?? 0),
  startedOn: parseDate(p.StartedOn ?? p.startedOn),
  error: safeString(p.Error ?? p.error),
  failedOn: parseDate(p.FailedOn ?? p.failedOn),
} : null);

const parseTurnRecord = (r) => (r ? {
  turn: Number(r.Turn ?? r.turn ?? 0),
  turnID: safeString(r.TurnID ?? r.turnID),
  prompt: safeString(r.Prompt ?? r.prompt),
  intent: safeString(r.Intent ?? r.intent),
  startedOn: parseDate(r.StartedOn ?? r.startedOn),
  completedOn: parseDate(r.CompletedOn ?? r.completedOn),
  // The ChatQueryShapingResponse as the pipeline produced it — applied through the
  // same path a live turn's response takes.
  response: r.Response ?? r.response ?? null,
} : null);

/** Normalizes an AssistantSessionDocument; `snapshot` is the dialog's own session shape (buildSession). */
export const parseSessionDocument = (doc) => {
  const turnCount = Number(doc?.TurnCount ?? doc?.turnCount ?? 0);
  const snapshotTurn = Number(doc?.SnapshotTurn ?? doc?.snapshotTurn ?? 0);
  const lastTurn = parseTurnRecord(doc?.LastTurn ?? doc?.lastTurn);
  return {
    sessionKey: safeString(doc?.SessionKey ?? doc?.sessionKey),
    profile: safeString(doc?.Profile ?? doc?.profile),
    diagnosticSessionID: safeString(doc?.DiagnosticSessionID ?? doc?.diagnosticSessionID),
    snapshot: doc?.Snapshot ?? doc?.snapshot ?? null,
    snapshotTurn,
    pendingTurn: parsePendingTurn(doc?.PendingTurn ?? doc?.pendingTurn),
    lastTurn,
    turnCount,
    lastTurnOn: parseDate(doc?.LastTurnOn ?? doc?.lastTurnOn),
    updatedOn: parseDate(doc?.UpdatedOn ?? doc?.updatedOn),
    // The last turn finished but its response never reached the dialog's state.
    hasUnappliedTurn: !!lastTurn?.response && snapshotTurn < turnCount,
  };
};

const parseOpenResult = (res) => (res ? {
  info: parseSessionInfo(res.Info ?? res.info),
  document: parseSessionDocument(res.Document ?? res.document),
} : null);

/** The group's sessions, most recently updated first (archived ones when asked). */
export const listAssistantSessions = async (host, includeArchived = false) => {
  const rows = throwIfFrameworkException(await q_List.getAll(toHostRequest(host, { includeArchived: !!includeArchived })));
  return (Array.isArray(rows) ? rows : []).map(parseSessionInfo).filter(Boolean);
};

/** The group's newest active session with its document, or a new one — what the dialog opens with. */
export const openOrCreateAssistantSession = async (host, title, apiVersion) => parseOpenResult(
  throwIfFrameworkException(await q_OpenOrCreate.getFirstOrDefault(toHostRequest(host, { title: safeString(title), apiVersion: safeString(apiVersion) || null }))),
);

/** One session with its document; null when it no longer exists. */
export const loadAssistantSession = async (sessionKey) => parseOpenResult(
  throwIfFrameworkException(await q_Load.getFirstOrDefault({ sessionKey })),
);

/** A new session in the group (the picker's "New session"). */
export const createAssistantSession = async (host, title, apiVersion) => parseSessionInfo(
  throwIfFrameworkException(await update('AssistantSession_Create', toHostRequest(host, { title: safeString(title), apiVersion: safeString(apiVersion) || null }), false)),
);

export const renameAssistantSession = async (sessionKey, title) => parseSessionInfo(
  throwIfFrameworkException(await update('AssistantSession_Rename', { sessionKey, value: safeString(title) }, false)),
);

/**
 * Moves a session to another host group (same profile): a session begun on a record
 * that did not exist yet follows the record once it is saved. Quiet on failure.
 */
export const rehostAssistantSession = async (sessionKey, host, title) => {
  if (!sessionKey) return null;
  try {
    const res = await queryOther('Update', 'AssistantSession_Rehost', 'POST', {
      sessionKey,
      hostEntity: safeString(host?.hostEntity).trim() || null,
      hostEntityID: Number(host?.hostEntityID) > 0 ? Number(host.hostEntityID) : null,
      hostSubKey: safeString(host?.hostSubKey).trim() || null,
      title: safeString(title).trim() || null,
    });
    return res?.data ? parseSessionInfo(res.data) : null;
  } catch {
    return null;
  }
};

export const setAssistantSessionStatus = async (sessionKey, status) => parseSessionInfo(
  throwIfFrameworkException(await update('AssistantSession_SetStatus', { sessionKey, value: safeString(status) }, false)),
);

/**
 * Writes the dialog's current session object to the server's document (between turns;
 * the server skips it while a turn runs). Quiet on failure — a sync is best effort and
 * the next turn carries the state anyway — so it goes through queryOther, which never
 * raises the exception dialog.
 */
export const saveAssistantSessionSnapshot = async (sessionKey, snapshotJson) => {
  try {
    const res = await queryOther('Update', 'AssistantSession_SaveSnapshot', 'POST', { sessionKey, value: safeString(snapshotJson) });
    return res?.data ? parseSessionInfo(res.data) : null;
  } catch {
    return null;
  }
};

export const deleteAssistantSession = async (sessionKey) => {
  const res = throwIfFrameworkException(await update('AssistantSession_Delete', { sessionKey }, false));
  return res === true || safeString(res).toUpperCase() === 'TRUE';
};

// ── The session's files ─────────────────────────────────────────────────────────
//
// What the user pastes, drops or attaches lives on the session as a document: stored
// once, read into markdown (a PDF in the background, a large one only after the
// go-ahead), listed to the assistant under SESSION FILES, and kept for later sessions
// with one click (promote → library entry, the key joining the design references).

export const DOCUMENT_STATUS_PENDING = 'pending';
export const DOCUMENT_STATUS_PROCESSING = 'processing';
export const DOCUMENT_STATUS_READY = 'ready';
export const DOCUMENT_STATUS_FAILED = 'failed';
export const DOCUMENT_STATUS_REFUSED = 'refused';
export const DOCUMENT_STATUS_HELD = 'held';

export const DOCUMENT_KIND_PDF = 'pdf';
export const DOCUMENT_KIND_IMAGE = 'image';
export const DOCUMENT_KIND_TABLE = 'table';
export const DOCUMENT_KIND_TEXT = 'text';
export const DOCUMENT_KIND_PASTE = 'paste';

/**
 * An update whose failure belongs on the chip, not in a modal: the server's message
 * (an EntityValidationException's text) becomes the thrown Error's message.
 */
const quietUpdate = async (action, body) => {
  const res = await queryOther('Update', action, 'POST', body);
  if (res?.queryError) {
    const raw = safeString(res.queryError);
    const json = raw.slice(raw.indexOf('{'));
    let message = '';
    try { message = safeString(JSON.parse(json)?.Message).trim(); } catch { message = ''; }
    throw new Error(message || raw);
  }
  return throwIfFrameworkException(res?.data);
};

/** Normalizes an AssistantDocumentModel (PascalCase from the server) to the chip's shape. */
export const parseSessionDocumentModel = (d) => (d ? {
  id: safeString(d.Id ?? d.id),
  name: safeString(d.Name ?? d.name),
  kind: safeString(d.Kind ?? d.kind).toLowerCase(),
  mediaType: safeString(d.MediaType ?? d.mediaType),
  fileName: safeString(d.FileName ?? d.fileName),
  status: safeString(d.Status ?? d.status).toLowerCase() || DOCUMENT_STATUS_PENDING,
  holdReason: safeString(d.HoldReason ?? d.holdReason),
  pageCount: Number(d.PageCount ?? d.pageCount ?? 0),
  pagesDone: Number(d.PagesDone ?? d.pagesDone ?? 0),
  bytes: Number(d.Bytes ?? d.bytes ?? 0),
  digest: safeString(d.Digest ?? d.digest),
  failure: safeString(d.Failure ?? d.failure),
  addedBy: safeString(d.AddedBy ?? d.addedBy),
  added: parseDate(d.Added ?? d.added),
  note: safeString(d.Note ?? d.note),
  describe: safeString(d.Describe ?? d.describe),
} : null);

const parseDocumentList = (rows) => (Array.isArray(rows) ? rows : [rows]).map(parseSessionDocumentModel).filter((d) => d && d.id);

/** The session's files with each one's standing. */
export const listAssistantSessionDocuments = async (sessionKey) => {
  if (!sessionKey) return [];
  return parseDocumentList(throwIfFrameworkException(await q_Documents.getAll({ sessionKey })));
};

/**
 * Files onto the session: each goes up in pieces (so size is no object) and the
 * session takes them up in one call. onProgress(file, sent, total) reports each file's
 * upload; the returned chips stand as the server has them (a PDF still reading).
 */
export const attachAssistantSessionFiles = async (sessionKey, files, { onProgress } = {}) => {
  const list = Array.from(files ?? []).filter(Boolean);
  if (!sessionKey || list.length === 0) return [];
  const uploads = [];
  for (const file of list) {
    uploads.push(await uploadFileInChunks(file, { onProgress: (sent, total) => onProgress?.(file, sent, total) }));
  }
  return parseDocumentList(await quietUpdate('AssistantSession_Attach', { sessionKey, uploads }));
};

/** Pasted text (or HTML) onto the session as a document read exactly. */
export const pasteAssistantSessionText = async (sessionKey, { name, text, html } = {}) => parseSessionDocumentModel(
  await quietUpdate('AssistantSession_Paste', { sessionKey, name: safeString(name) || null, text: safeString(text) || null, html: safeString(html) || null }),
);

/** Screenshots (data URLs) onto the session as screenshot-N images. */
export const pasteAssistantSessionImages = async (sessionKey, dataUrls) => {
  const images = (Array.isArray(dataUrls) ? dataUrls : []).map(safeString).filter(Boolean);
  if (!sessionKey || images.length === 0) return [];
  return parseDocumentList(await quietUpdate('AssistantSession_PasteImages', { sessionKey, images }));
};

/** The go-ahead on a held (large) document, or "try again" on one that failed. */
export const confirmAssistantSessionDocument = async (sessionKey, id) => parseSessionDocumentModel(
  await quietUpdate('AssistantSession_ConfirmDocument', { sessionKey, id }),
);

export const removeAssistantSessionDocument = async (sessionKey, id) => {
  const res = await quietUpdate('AssistantSession_RemoveDocument', { sessionKey, id });
  return res === true || safeString(res).toUpperCase() === 'TRUE';
};

/**
 * Keeps a session file as a library entry (a design reference) — returns the saved
 * CustomizationAssetModel; the caller declares its key on the references lane. The
 * server's refusal (key taken, kind not kept) is the thrown Error's message.
 */
export const promoteAssistantSessionDocument = async (sessionKey, id, { key, description } = {}) => quietUpdate(
  'AssistantSession_Promote', { sessionKey, id, key: safeString(key).trim() || null, description: safeString(description).trim() || null },
);

/**
 * A session file's bytes as an object URL ({ url } or { queryError }) — the chip's
 * thumbnail / view / download. The API wants the bearer token, so a plain <img src>
 * cannot reach it; the caller revokes the URL when done.
 */
export const fetchAssistantSessionDocument = async (sessionKey, id) => {
  if (!sessionKey || !id) return { queryError: 'no document' };
  return queryOtherBlobUrl('AssistantSessionFiles', 'Document', `?sessionKey=${encodeURIComponent(sessionKey)}&id=${encodeURIComponent(id)}`);
};
